import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { timeAgo } from '../../pipes/utils/dateago.utils';
import { formatDuration } from '../../pipes/utils/duration.utils';
// import { MoneyModel } from ... (Your imports here)

@Component({
  selector: 'app-dashboard-stats-card',
  templateUrl: './dashboard-stats-card.component.html',
  styleUrls: ['./dashboard-stats-card.component.css'],
})
export class DashboardStatsCardComponent implements OnChanges {
  // Inputs
  @Input() growth: number;
  @Input() title: string;
  @Input() type: 'percent' | 'number' | 'currency' | 'dateAgo' | 'custom' | 'duration' | 'text' = 'number';

  // Unit Config
  @Input() unit: string;
  @Input() unitPlural: string;

  // New Input: Fallback currency if a raw number is passed
  @Input() currencyCode: string = 'USD';

  // Style & Config
  @Input() color: 'primary' | 'success' | 'warning' | 'info' | 'white' | 'primary-solid' = 'white';
  @Input() dashNull: boolean = false;
  @Input() growthTooltip: string;

  // Unified State
  displayValue: any;
  complexLabel: string;
  isMoneyModel: boolean = false; // Controls the *ngIf in your template
  activeCurrencyCode: string = 'USD'; // Stores the detected currency (EGP, USD, etc)

  // Internal storage for count
  _count: any;

  // 1. Logic removed from setter to fix lifecycle bugs
  @Input() set count(value: any) {
    this._count = value;
  }

  constructor() { }

  // 2. Logic moved here. Runs when 'count', 'type', or 'currencyCode' changes.
  ngOnChanges(changes: SimpleChanges): void {
    this.processData();
  }

  private processData() {
    const value = this._count;

    // Handle Nulls
    if (value === null || value === undefined || value === '') {
      this.displayValue = null;
      return;
    }

    switch (this.type) {
      case 'currency':
        // Check if it's your MoneyModel object
        const isObject = value && typeof value === 'object' && ('currency' in value);

        if (isObject) {
          // It is an Object: Keep it as object so template can read .value / .amount
          this.isMoneyModel = true;
          this.displayValue = value;
          this.activeCurrencyCode = value.currency || this.currencyCode;
        } else {
          // It is a Number: Parse it, set model to false
          this.isMoneyModel = false;
          this.displayValue = this.parseNumber(value);
          this.activeCurrencyCode = this.currencyCode;
        }
        break;

      case 'dateAgo':
        this.handleDateAgo(value);
        break;

      case 'duration':
        this.handleDuration(value);
        break;

      case 'number':
      case 'percent':
        this.displayValue = this.parseNumber(value);
        break;

      default:
        this.displayValue = value;
        break;
    }
  }

  // --- Helpers ---
  private parseNumber(val: any): number {
    const num = parseFloat(String(val));
    return isNaN(num) ? 0 : num;
  }

  private handleDateAgo(value: any) {
    if (isNaN(parseFloat(value))) {
      this.displayValue = null;
      this.complexLabel = String(value);
      return;
    }
    const result = this.transformIntoAgoFormat(Number(value) * 1000);
    this.displayValue = result.count;
    this.complexLabel = result.label;
  }

  private handleDuration(value: any) {
    if (isNaN(parseFloat(value))) {
      this.displayValue = null;
      this.complexLabel = String(value);
      return;
    }
    const result = this.transformIntoDurationFormat(Number(value));
    this.displayValue = result.count;
    this.complexLabel = result.label;
  }

  transformIntoAgoFormat(ms: number) {
    const timeAgoValue: string = timeAgo(ms);
    const split = timeAgoValue?.toString()?.split(' ') || [];
    if (!parseFloat(split[0])) return { count: null, label: timeAgoValue };
    return { count: parseFloat(split[0]), label: split.slice(1).join(' ') };
  }

  transformIntoDurationFormat(seconds: number) {
    const timeDurationValue: string = formatDuration(seconds * 1000);
    const split = timeDurationValue?.toString()?.split(' ') || [];
    if (!parseFloat(split[0]) && split[0] !== '0') return { count: null, label: timeDurationValue };
    return { count: parseFloat(split[0]), label: split[1] };
  }

  // Styling getters
  get gradientClasses() {
    switch (this.color) {
      case 'primary': return 'from-blue-50 to-transparent';
      case 'primary-solid': return 'from-blue-100 to-transparent';
      case 'success': return 'from-emerald-50 to-transparent';
      case 'warning': return 'from-yellow-50 to-transparent';
      case 'info': return 'from-purple-50 to-transparent';
      default: return 'from-gray-50 to-transparent';
    }
  }

  get iconClasses() {
    switch (this.color) {
      case 'primary': return 'bg-blue-50 text-blue-600';
      case 'success': return 'bg-emerald-50 text-emerald-600';
      case 'warning': return 'bg-yellow-50 text-yellow-600';
      case 'info': return 'bg-purple-50 text-purple-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  }
}